import { Reservation } from 'modules/arena/reservations/types';
import { RESERVATION_STATUS_META } from 'modules/arena/reservations/content';
import { Card } from 'shared/components/ui/Card';
import { Badge } from 'shared/components/ui/Badge';
import { Button } from 'shared/components/ui/Button';
import { Avatar } from 'shared/components/ui/Avatar';
import { formatBRL } from 'shared/utils/format';

interface ReservationDetailPanelProps {
  reservation: Reservation;
  onConfirm: (id: string) => void;
  onCancel: (id: string) => void;
  onMarkNoShow: (id: string) => void;
  onMarkCompleted: (id: string) => void;
  onExtendDeadline: (id: string) => void;
  onChargeRemaining: (id: string) => void;
  /** Recepção confirma, marca presença e cobra — mas não cancela reservas pela arena. */
  canCancel?: boolean;
}

const PARTICIPANT_STATUS_LABEL = (paid: number, due: number) =>
  paid >= due ? { text: 'pago', tone: 'text-success' } : { text: 'pendente', tone: 'text-warning' };

export function ReservationDetailPanel({
  reservation,
  onConfirm,
  onCancel,
  onMarkNoShow,
  onMarkCompleted,
  onExtendDeadline,
  onChargeRemaining,
  canCancel = true,
}: ReservationDetailPanelProps) {
  const statusMeta = RESERVATION_STATUS_META[reservation.status];
  const isTerminal = ['cancelled', 'expired', 'completed', 'no_show'].includes(reservation.status);

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
            Reserva {reservation.id}
          </p>
          <h3 className="mt-1 font-heading text-lg font-semibold text-ink">
            {reservation.courtName} · {reservation.sport}
          </h3>
          <p className="text-sm text-ink-secondary">
            {reservation.dateLabel} · {reservation.timeLabel} ({reservation.durationLabel})
          </p>
        </div>
        <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge>
      </div>

      {reservation.expiresAtLabel && (
        <p className="mt-3 rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning">
          Expira às {reservation.expiresAtLabel} se o pagamento não for concluído.
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-ink-secondary">Valor total</p>
          <p className="font-medium text-ink">{formatBRL(reservation.totalAmount)}</p>
        </div>
        <div>
          <p className="text-xs text-ink-secondary">Taxa da plataforma (congelada)</p>
          <p className="font-medium text-ink">{formatBRL(reservation.platformFeeSnapshot)}</p>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-ink-secondary">Política de cancelamento (congelada)</p>
          <p className="font-medium text-ink">{reservation.cancellationPolicySnapshot}</p>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-ink-secondary">Criada em</p>
          <p className="font-medium text-ink">{reservation.createdAtLabel}</p>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
          Participantes
        </p>
        <div className="mt-2 flex flex-col gap-2">
          {reservation.participants.map((participant) => {
            const paidStatus = PARTICIPANT_STATUS_LABEL(participant.amountPaid, participant.amountDue);

            return (
              <div key={participant.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Avatar name={participant.name} size="sm" />
                  <span className="text-ink">{participant.name}</span>
                </div>
                <span className={paidStatus.tone}>
                  {formatBRL(participant.amountDue)} · {participant.method} · {paidStatus.text}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {isTerminal ? (
        <p className="mt-5 text-xs text-ink-secondary">Estado final — sem ações disponíveis.</p>
      ) : (
        <div className="mt-5 flex flex-wrap gap-2">
          {reservation.status === 'pending' && (
            <Button className="flex-1" onClick={() => onConfirm(reservation.id)}>
              Confirmar reserva
            </Button>
          )}

          {reservation.status === 'awaiting_payment' && (
            <>
              <Button variant="ghost" className="flex-1" onClick={() => onExtendDeadline(reservation.id)}>
                Estender prazo
              </Button>
              <Button className="flex-1" onClick={() => onChargeRemaining(reservation.id)}>
                Cobrar restante
              </Button>
            </>
          )}

          {reservation.status === 'confirmed' && (
            <>
              <Button variant="ghost" className="flex-1" onClick={() => onMarkNoShow(reservation.id)}>
                Marcar no-show
              </Button>
              <Button className="flex-1" onClick={() => onMarkCompleted(reservation.id)}>
                Marcar concluída
              </Button>
            </>
          )}

          {canCancel && (
            <Button
              variant="ghost"
              className="w-full border-danger text-danger hover:bg-danger-soft"
              onClick={() => onCancel(reservation.id)}
            >
              Cancelar reserva (pela arena)
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
